import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import BabasahebPortrait from "../components/BabasahebPortrait";

// Listed in chronological order — by year of birth.
const REFORMERS = [
  {
    id: "ashoka",
    name: "Samrat Ashoka",
    title: "The Great Emperor of India",
    dates: "304 BCE — 232 BCE",
    role: "Mauryan Emperor · First Royal Patron of Buddhism · Ruler of Dhamma",
    image:
      "/images/p79lwkvj-f4c400bb-78f4-417c-93ad-866e29fe3e97.png",
    quote:
      "All men are my children. As I desire for my own children that they may obtain welfare and happiness, so do I desire it for all men.",
    bio: [
      "Samrat Ashoka, the third emperor of the Maurya dynasty, ruled most of the Indian subcontinent in the 3rd century BCE. After the bloody Kalinga war he renounced violence and embraced the Buddha Dhamma, a moment that changed the moral compass of an empire.",
      "He spread the Buddha's teachings across Asia, established hospitals, planted trees on highways, dug wells, and inscribed his Dhamma edicts on rocks and pillars. The Ashoka Chakra at the centre of the Indian flag, and the Lion Capital that is India's national emblem, both come from him. For Ambedkarites, Samrat Ashoka represents the highest ideal of governance rooted in Dhamma.",
    ],
  },
  {
    id: "shivaji",
    name: "Chhatrapati Shivaji Maharaj",
    title: "Founder of Swarajya",
    dates: "19 February 1630 — 3 April 1680",
    role: "Founder of the Maratha Empire · Visionary Ruler of Just Governance",
    image:
      "/images/bvu9v8br-1567b038-0493-4561-b578-a0ab5b0d33e2.jpeg",
    quote: "Swarajya is my birthright, and I shall have it, through just rule and courage.",
    bio: [
      "A brave and visionary ruler, Chhatrapati Shivaji Maharaj founded the Maratha Empire in 17th-century India and established the ideal of Swarajya, self-governance rooted in justice, religious tolerance and protection of the common people.",
      "His model of people-centred rule, inclusive administration and deep respect for every faith continues to inspire the Ambedkarite vision of a just and enlightened society.",
    ],
  },
  {
    id: "phule",
    name: "Mahatma Jyotirao Phule",
    title: "Mahatma",
    dates: "11 April 1827 — 28 November 1890",
    role: "Social Reformer · Educator · Champion of the Oppressed",
    image:
      "/images/vqj1e8g6-804f88e5-cf8c-489e-a6b2-1bde052cec84.jpeg",
    quote:
      "Without education, intellect is lost; without intellect, morality is lost; without morality, progress is lost.",
    bio: [
      "Jyotirao Govindrao Phule was a pioneering Indian social activist, thinker and writer from Maharashtra. With his wife Savitribai Phule, he opened the first school for girls in India in 1848 — a revolutionary act in a society where education was denied to women and lower castes.",
      "Founder of the Satyashodhak Samaj (Society of Truth Seekers), Phule relentlessly challenged caste hierarchy, religious orthodoxy and gender injustice. Dr. Ambedkar regarded him as one of his three gurus, and his life's work forms a direct foundation for the Ambedkarite movement we carry forward.",
    ],
  },
  {
    id: "savitribai",
    name: "Savitribai Phule",
    title: "Krantijyoti",
    dates: "3 January 1831 — 10 March 1897",
    role: "India's First Woman Teacher · Poet · Champion of Women's Education",
    image:
      "/images/o42d6ahk-68ea93da-69e9-4f2d-99df-0f81b92be459.png",
    image_position: "22% 35%",
    image_zoom: 1.5,
    quote:
      "Awake, arise and educate. Smash traditions, liberate.",
    bio: [
      "Savitribai Phule was India's first woman teacher and a fearless social reformer. Alongside her husband Mahatma Jyotirao Phule, she opened the first school for girls in Pune in 1848 and went on to start seventeen more schools, for girls, for the most marginalised, and for the children whom society had forgotten.",
      "She faced stones, dung and abuse on her daily walk to the school, yet she carried a second sari with her, changed when she arrived, and taught with a smile. A poet, nurse and tireless campaigner against caste injustice, Savitribai is the foremother of every educated woman in India today, and a guiding light for the Ambedkarite tradition we carry forward.",
    ],
  },
  {
    id: "shahu",
    name: "Rajarshi Chhatrapati Shahu Maharaj",
    title: "Rajarshi",
    dates: "26 June 1874 — 6 May 1922",
    role: "King of Kolhapur · Pioneer of Reservation · Champion of the Bahujan Samaj",
    image:
      "/images/u7cawx84-97786068-3356-4c0f-ab90-f3b419dc6330.png",
    quote: "The King of the People, forever an inspiration.",
    bio: [
      "Chhatrapati Shahu Maharaj of Kolhapur was a visionary king and one of the earliest royal patrons of social justice in modern India. Crowned in 1894, he turned the Kolhapur state into a laboratory of progressive reform: introducing 50% reservation in education and government jobs for non-Brahmin and so-called depressed classes as early as 26 July 1902 — half a century before independent India would consider the same idea.",
      "He opened hostels and schools for every community he could reach, abolished discriminatory bonded-labour practices, banned untouchability in state institutions, and personally funded the higher education of the young Bhimrao Ambedkar, the boy who would become Babasaheb. He met Babasaheb on more than one occasion and famously declared him the future leader of India's untouchables. Without Shahu Maharaj's foresight, generosity and political courage, the Ambedkarite movement might never have had the air it needed to breathe.",
      "Known to his subjects as Rajarshi, the king who is also a sage, Shahu Maharaj remains one of the foremost reformers of the Bahujan tradition: a king who used his throne not to defend privilege, but to dismantle it.",
    ],
  },
  {
    id: "periyar",
    name: "Periyar E.V. Ramasamy",
    title: "Thanthai Periyar",
    dates: "17 September 1879 — 24 December 1973",
    role: "Social Reformer · Rationalist · Champion of Self-Respect & Social Justice",
    image:
      "/images/stg6ijqo-a859a2d4-fb40-4e44-8a5b-5f6278ccfba3.jpeg",
    quote: "We must aim at a society where every individual is treated with equality, dignity and self-respect.",
    bio: [
      "Erode Venkatappa Ramasamy, known as Periyar, was a towering Tamil social reformer, rationalist and the founder of the Self-Respect Movement and Dravidar Kazhagam.",
      "Throughout his long life, Periyar fought relentlessly against caste discrimination, gender inequality and religious orthodoxy. His insistence on rationalism, dignity and equality for all, regardless of caste, creed or gender, remains a foundational pillar of the Ambedkarite tradition we carry forward.",
    ],
  },
  {
    id: "ambedkar",
    name: "Dr. B.R. Ambedkar",
    title: "Babasaheb",
    dates: "14 April 1891 — 6 December 1956",
    role: "Father of the Indian Constitution · Champion of Equality",
    image: null, // uses BabasahebPortrait component
    quote:
      "I measure the progress of a community by the degree of progress which women have achieved.",
    bio: [
      "A jurist, economist, social reformer and political leader, Dr. Bhimrao Ramji Ambedkar chaired the Drafting Committee of the Indian Constitution. Educated in Bombay, New York (Columbia) and London (LSE), he dedicated his life to the abolition of untouchability and the empowerment of the oppressed.",
      "On 14 October 1956, he embraced Buddhism with over 500,000 of his followers at Deekshabhoomi, Nagpur, a moment that transformed the modern Buddhist revival. His ideals of liberty, equality and fraternity form the spiritual and civic foundation of ABGN-UK.",
    ],
  },
  {
    id: "ramabai",
    name: "Mata Ramabai Ambedkar",
    title: "Mata",
    dates: "7 February 1898 — 27 May 1935",
    role: "Devoted Companion · Quiet Strength Behind Babasaheb's Mission",
    image:
      "/images/wubor5bc-whatsapp-image-2026-05-06-at-05-19-09.jpeg",
    quote:
      "A home filled with sacrifice and learning is the cradle of a nation's awakening.",
    bio: [
      "Mata Ramabai Ambedkar, affectionately known as Ramai, was the first wife and lifelong companion of Babasaheb Dr. B.R. Ambedkar. Born in a humble fisherfolk family in the Konkan, she shouldered the family's hardships with extraordinary patience while Babasaheb pursued his studies abroad in New York and London.",
      "She raised their children, ran the household on the slenderest of means, and stood unshaken through poverty, the loss of four young children, and years of separation. Her quiet, sacrificial strength gave Babasaheb the foundation from which a movement was born. Every Ambedkarite household honours her memory as the silent half of a revolution, the mother who carried the cradle of our awakening.",
    ],
  },
  {
    id: "annabhau",
    name: "Anna Bhau Sathe",
    title: "Lokshahir",
    dates: "1 August 1920 — 18 July 1969",
    role: "People's Poet · Novelist · Social Revolutionary",
    image:
      "/images/mu0u5i90-3c3d1e73-15f0-4597-b6df-55efedd8e23b.jpeg",
    quote:
      "This earth is not balanced on the hood of a serpent, it is held up by the labour of the working class.",
    bio: [
      "Tukaram Bhaurao Sathe, popularly known as Anna Bhau Sathe, was a Marathi novelist, balladeer (shahir) and social activist whose writings gave voice to the struggles of workers, farmers and the Dalit community.",
      "Author of 35 novels and hundreds of powadas and lavanis, his fearless storytelling and lifelong commitment to the oppressed make him one of the most cherished voices of the Ambedkarite cultural renaissance.",
    ],
  },
];

export default function Reformers() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash]);

  return (
    <>
      <section className="bg-navy text-pearl py-24" data-testid="reformers-hero">
        <div className="section-container">
          <p className="eyebrow text-gold">Those who lit the path</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-tight max-w-4xl">
            The reformers who shape our conscience.
          </h1>
          <p className="mt-6 max-w-2xl text-pearl/80 text-lg leading-relaxed">
            ABGN-UK draws inspiration from a lineage of Indian visionaries, from the great
            Buddhist emperor Samrat Ashoka to the people's poet Anna Bhau Sathe, each one a beacon
            of justice, dignity and self-respect across more than two thousand years.
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.25em] text-gold/80">
            Listed chronologically · By year of birth
          </p>
        </div>
      </section>

      <section className="py-24 bg-pearl">
        <div className="section-container space-y-24">
          {REFORMERS.map((r, i) => (
            <motion.article
              key={r.id}
              id={r.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start"
              data-testid={`reformer-${r.id}`}
            >
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                {r.id === "ambedkar" ? (
                  <BabasahebPortrait />
                ) : (
                  <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-navy/5 border border-navy/10">
                    <img
                      src={r.image}
                      alt={r.name}
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: r.image_position || "center",
                        transform: r.image_zoom ? `scale(${r.image_zoom})` : undefined,
                        transformOrigin: r.image_position || "center",
                      }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 ring-1 ring-navy/10 pointer-events-none" />
                  </div>
                )}
                <div className="mt-4 flex items-center gap-3">
                  <span className="font-display text-gold text-2xl leading-none">{i + 1}</span>
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{r.dates}</span>
                </div>
                {r.image_caption && (
                  <p className="mt-3 text-xs italic text-slate-500 leading-relaxed">{r.image_caption}</p>
                )}
              </div>

              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <p className="eyebrow">{r.title}</p>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">{r.name}</h2>
                <p className="mt-3 text-saffron text-sm font-medium">{r.role}</p>
                <blockquote className="mt-8 border-l-4 border-gold pl-6 font-display italic text-2xl text-navy leading-snug">
                  "{r.quote}"
                </blockquote>
                <div className="mt-8 space-y-4 text-slate-700 leading-relaxed">
                  {r.bio.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="py-20 bg-navy text-pearl">
        <div className="section-container text-center">
          <p className="eyebrow text-gold">In their footsteps</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl max-w-3xl mx-auto">
            We carry the torch they lit, across the UK, for the generations to come.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-pearl/70 italic">Jai Bhim · Namo Buddhāya</p>
        </div>
      </section>
    </>
  );
}
